/**
 * FileMakerPrintableToXML.ts - Translatr plugin for FMwvTool
 * 
 * Converts FileMaker "printable" script format to FileMaker XML
 * 
 * Example input:
 *   Set Variable [ $url ; "http://localhost:3008" ]
 *   Set Web Viewer [ Object Name: "wvTool" ; URL: $url ]
 * 
 * Output: FileMaker XML that can be pasted into Script Workspace
 */

import type { Plugin } from '../../PluggrTypes';

// FileMaker Script Step IDs
const STEP_IDS = {
  COMMENT: 89,
  SET_VARIABLE: 141,
  SET_FIELD: 76,
  SET_WEB_VIEWER: 143,
  IF: 68,
  ELSE: 69,
  END_IF: 70,
  LOOP: 71,
  EXIT_LOOP_IF: 72,
  END_LOOP: 73,
  PERFORM_SCRIPT: 1,
  PERFORM_JAVASCRIPT_IN_WEB_VIEWER: 142,
};

interface ScriptStep {
  id: number;
  name: string;
  enable?: boolean;
  params?: Record<string, string>;
}

export class FileMakerPrintableToXML {
  
  /**
   * Parse printable format and convert to XML
   */
  convertToXML(printableText: string): string {
    const steps = this.parsePrintableText(printableText);
    const xmlSteps = steps.map(step => this.stepToXML(step)).join('\n');
    
    return `<?xml version="1.0" encoding="UTF-8"?>
<fmxmlsnippet type="FMObjectList">
    <Script includeInMenu="False" runFullAccess="False" id="1" name="Converted Script">
${xmlSteps}
    </Script>
</fmxmlsnippet>`;
  }
  
  /**
   * Parse printable text into structured steps
   */
  private parsePrintableText(text: string): ScriptStep[] {
    const lines = text.split('\n');
    const steps: ScriptStep[] = [];
    
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      
      // Comment
      if (trimmed.startsWith('#') || trimmed.startsWith('//')) {
        steps.push({
          id: STEP_IDS.COMMENT,
          name: '# (comment)',
          params: { text: trimmed.replace(/^[#\/]+\s*/, '') }
        });
        continue;
      }
      
      // Set Variable [ $var ; Value: "value" ] or [ $var ; "value" ]
      const setVarMatch = trimmed.match(/Set Variable\s*\[\s*(\$\w+)\s*;\s*(?:Value:\s*)?(.+?)\s*\]/);
      if (setVarMatch) {
        steps.push({
          id: STEP_IDS.SET_VARIABLE,
          name: 'Set Variable',
          params: {
            name: setVarMatch[1],
            value: setVarMatch[2].replace(/^["']|["']$/g, '') // Remove quotes
          }
        });
        continue;
      }
      
      // Set Web Viewer [ Object Name: "name" ; URL: expression ]
      const setWebViewerMatch = trimmed.match(/Set Web Viewer\s*\[\s*Object Name:\s*"([^"]+)"\s*;\s*URL:\s*(.+?)\s*\]/);
      if (setWebViewerMatch) {
        steps.push({
          id: STEP_IDS.SET_WEB_VIEWER,
          name: 'Set Web Viewer',
          params: {
            objectName: setWebViewerMatch[1],
            url: setWebViewerMatch[2]
          }
        });
        continue;
      }
      
      // If [ condition ]
      const ifMatch = trimmed.match(/If\s*\[\s*(.+?)\s*\]/);
      if (ifMatch) {
        steps.push({
          id: STEP_IDS.IF,
          name: 'If',
          params: { calculation: ifMatch[1] }
        });
        continue;
      }
      
      // Simple steps
      if (trimmed === 'Else') {
        steps.push({ id: STEP_IDS.ELSE, name: 'Else' });
        continue;
      }
      if (trimmed === 'End If') {
        steps.push({ id: STEP_IDS.END_IF, name: 'End If' });
        continue;
      }
      if (trimmed === 'Loop') {
        steps.push({ id: STEP_IDS.LOOP, name: 'Loop' });
        continue;
      }
      if (trimmed === 'End Loop') {
        steps.push({ id: STEP_IDS.END_LOOP, name: 'End Loop' });
        continue;
      }
      
      // Exit Loop If [ condition ]
      const exitLoopMatch = trimmed.match(/Exit Loop If\s*\[\s*(.+?)\s*\]/);
      if (exitLoopMatch) {
        steps.push({
          id: STEP_IDS.EXIT_LOOP_IF,
          name: 'Exit Loop If',
          params: { calculation: exitLoopMatch[1] }
        });
        continue;
      }
    }
    
    return steps;
  }
  
  /**
   * Convert step to XML
   */
  private stepToXML(step: ScriptStep, indent: string = '        '): string {
    const enable = step.enable !== false ? 'True' : 'False';
    
    // Comment
    if (step.id === STEP_IDS.COMMENT) {
      return `${indent}<Step enable="${enable}" id="${step.id}" name="# (comment)">
${indent}    <Text>${this.escapeXML(step.params?.text || '')}</Text>
${indent}</Step>`;
    }
    
    // Set Variable
    if (step.id === STEP_IDS.SET_VARIABLE) {
      return `${indent}<Step enable="${enable}" id="${step.id}" name="Set Variable">
${indent}    <Value>
${indent}        <Calculation><![CDATA[${step.params?.value || ''}]]></Calculation>
${indent}    </Value>
${indent}    <Repetition>
${indent}        <Calculation><![CDATA[1]]></Calculation>
${indent}    </Repetition>
${indent}    <Name>${step.params?.name || ''}</Name>
${indent}</Step>`;
    }
    
    // Set Web Viewer
    if (step.id === STEP_IDS.SET_WEB_VIEWER) {
      return `${indent}<Step enable="${enable}" id="${step.id}" name="Set Web Viewer">
${indent}    <ObjectName>
${indent}        <Calculation><![CDATA["${step.params?.objectName || ''}"]]></Calculation>
${indent}    </ObjectName>
${indent}    <WebURL>
${indent}        <Calculation><![CDATA[${step.params?.url || ''}]]></Calculation>
${indent}    </WebURL>
${indent}</Step>`;
    }
    
    // If / Exit Loop If
    if (step.id === STEP_IDS.IF || step.id === STEP_IDS.EXIT_LOOP_IF) {
      return `${indent}<Step enable="${enable}" id="${step.id}" name="${step.name}">
${indent}    <Calculation><![CDATA[${step.params?.calculation || ''}]]></Calculation>
${indent}</Step>`;
    }
    
    // Simple steps (Else, End If, End Loop)
    if ([STEP_IDS.ELSE, STEP_IDS.END_IF, STEP_IDS.END_LOOP].includes(step.id)) {
      return `${indent}<Step enable="${enable}" id="${step.id}" name="${step.name}"></Step>`;
    }
    
    // Loop
    if (step.id === STEP_IDS.LOOP) {
      return `${indent}<Step enable="${enable}" id="${step.id}" name="Loop">
${indent}    <FlushType value="Always"></FlushType>
${indent}</Step>`;
    }
    
    return `${indent}<Step enable="${enable}" id="${step.id}" name="${step.name}"></Step>`;
  }
  
  /**
   * Escape XML special characters
   */
  private escapeXML(text: string): string {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }
}

// Plugin definition
export const fileMakerPrintableToXMLPlugin: Plugin = {
  manifest: {
    id: 'filemaker-printable-to-xml',
    name: 'FileMaker Printable to XML',
    version: '1.0.0',
    description: 'Convert FileMaker printable script format to XML',
    author: 'Trusted Associates',
    main: 'FileMakerPrintableToXML.ts',
    category: 'filemaker',
    tags: ['filemaker', 'script', 'xml', 'conversion', 'translatr'],
  },
  
  // Plugin lifecycle
  onload: async (context) => {
    // Register command for FMwvTool
    context.registerCommand?.({
      id: 'convert-printable-to-xml',
      name: 'Convert FM Printable to XML',
      description: 'Convert FileMaker printable script format to XML',
      execute: (printableText: string) => {
        const converter = new FileMakerPrintableToXML();
        return converter.convertToXML(printableText);
      }
    });
    
    context.log?.info('FileMaker Printable to XML plugin loaded');
  },
  
  onunload: async () => {
    // Cleanup if needed
  }
};
